import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Layout,
    Card,
    Button,
    Upload,
    Modal,
    Form,
    Input,
    Select,
    Tag,
    message,
    Space,
    Popconfirm,
    Alert,
    Tooltip,
    Popover,
    Menu,
    Typography,
    DatePicker,
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
    PlusOutlined,
    EyeOutlined,
    DownloadOutlined,
    DeleteOutlined,
    EditOutlined,
    InboxOutlined,
    FileTextOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as actions from '../../store/actions/AuthActions';
import useUrlFilters from 'hooks/useUrlFilters';

const { Option } = Select;
const { Dragger } = Upload;
const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Section and attendance filters live in the URL (hr_ prefix: this is a tab of My Profile).
const URL_FILTERS = {
    section: { type: 'string', default: 'documents' },
    status: { type: 'string' },
    range: { type: 'dateRange', keys: ['date_from', 'date_to'] },
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 10 },
};

const HR = ({ isProfileLocked = false, employee }) => {
    const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS, { prefix: 'hr_' });
    const activeSection = urlFilters.section;
    const setActiveSection = (value) => setUrlFilters({ section: value });
    const profileLocked = useSelector((state) => state.auth.profileLocked);
    const employeeId = employee?.id;

    // Documents states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [editingDocument, setEditingDocument] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);

    // Attendance states
    const [attendanceData, setAttendanceData] = useState([]);
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [attendancePagination, setAttendancePagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const attendanceFilters = useMemo(() => {
        const searchFilters = {};
        if (urlFilters.status) {
            searchFilters.status = urlFilters.status;
        }
        if (urlFilters.range) {
            searchFilters.date_from = urlFilters.range[0].format('YYYY-MM-DD');
            searchFilters.date_to = urlFilters.range[1].format('YYYY-MM-DD');
        }
        return searchFilters;
    }, [urlFilters.status, urlFilters.range]);

    const menuItems = [
        { key: 'documents', icon: <FileTextOutlined />, label: 'Documents' },
        { key: 'attendance', icon: <ClockCircleOutlined />, label: 'Attendance' },
    ];

    // ==================== DOCUMENTS SECTION ====================

    const fetchDocuments = async () => {
        if (!employeeId) return;
        setDocumentsLoading(true);
        try {
            const response = await actions.loadEmployeeDocuments(employeeId);
            if (response && response.results) {
                const formattedDocs = response.results.map(doc => ({
                    id: doc.id,
                    name: doc.document_name || 'Unnamed Document',
                    type: doc.document_type || 'Other',
                    fileName: doc.document_name || 'file',
                    uploadDate: doc.uploaded_at ? doc.uploaded_at.split('T')[0] : new Date().toISOString().split('T')[0],
                    document_url: doc.document_url,
                    document_id: doc.document?.id,
                    status: doc.is_active ? 'verified' : 'pending',
                    description: doc.notes || '',
                    uploadedBy: doc.uploaded_by_name || 'Unknown',
                    uploaded_by_id: doc.uploaded_by,
                    is_active: doc.is_active
                }));
                setDocuments(formattedDocs);
            } else {
                setDocuments([]);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            message.error('Failed to load documents');
            setDocuments([]);
        } finally {
            setDocumentsLoading(false);
        }
    };

    useEffect(() => {
        if (employeeId) {
            fetchDocuments();
        }
    }, [employeeId]);

    const showModal = (document = null) => {
        if (profileLocked) {
            message.warning('Cannot upload documents - Profile is locked');
            return;
        }
        setEditingDocument(document);
        if (document) {
            form.setFieldsValue({
                name: document.name,
                type: document.type,
                description: document.description
            });
        }
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingDocument(null);
        form.resetFields();
        setFileList([]);
    };

    const handleSubmit = async (values) => {
        if (profileLocked) {
            message.warning('Cannot upload documents - Profile is locked');
            return;
        }

        setSubmitLoading(true);
        try {
            let documentId = editingDocument?.document_id;

            // Upload new file if selected
            if (fileList.length > 0 && fileList[0].originFileObj) {
                const uploadResult = await actions.uploadDocument(fileList[0].originFileObj);
                if (!uploadResult || !uploadResult.id) {
                    message.error('Failed to upload file');
                    return;
                }
                documentId = uploadResult.id;

                // Delete old document if replacing
                if (editingDocument?.document_id && editingDocument.document_id !== documentId) {
                    await actions.deleteDocument(editingDocument.document_id);
                }
            }

            // Validate that we have a document_id (required for new documents)
            if (!documentId && !editingDocument) {
                message.error('Please select a file to upload');
                return;
            }

            const payload = {
                employee: employeeId,
                document_id: documentId,
                document_type: values.type,
                notes: values.description || ''
            };

            let result;
            if (editingDocument) {
                result = await actions.updateEmployeeDocument(editingDocument.id, payload);
            } else {
                result = await actions.createEmployeeDocument(payload);
            }

            if (result) {
                handleCancel();
                fetchDocuments();
            }
        } catch (error) {
            console.error('Error submitting document:', error);
            message.error('Failed to save document');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleView = (record) => {
        if (record.document_url) {
            window.open(record.document_url, '_blank');
        } else {
            message.info('Document URL not available');
        }
    };

    const handleDownload = (record) => {
        if (profileLocked) {
            message.warning('Cannot download documents - Profile is locked');
            return;
        }
        if (record.document_url) {
            const link = document.createElement('a');
            link.href = record.document_url;
            link.download = record.fileName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            message.error('Document URL not available');
        }
    };

    const handleDelete = async (record) => {
        if (profileLocked) {
            message.warning('Cannot delete documents - Profile is locked');
            return;
        }
        try {
            const result = await actions.deleteEmployeeDocument(record.id);
            if (result) {
                // Also delete the underlying document file
                if (record.document_id) {
                    await actions.deleteDocument(record.document_id);
                }
                fetchDocuments();
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            message.error('Failed to delete document');
        }
    };

    const getDocumentStatusColor = (status) => {
        switch (status) {
            case 'verified': return 'green';
            case 'pending': return 'orange';
            case 'rejected': return 'red';
            case 'expired': return 'red';
            case 'renewal-required': return 'yellow';
            default: return 'default';
        }
    };

    const uploadProps = {
        fileList,
        onChange: ({ fileList: newFileList }) => setFileList(newFileList),
        beforeUpload: (file) => {
            const isValidType = file.type === 'application/pdf' || file.type.startsWith('image/');
            if (!isValidType) {
                message.error('You can only upload PDF or image files!');
                return false;
            }
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                message.error('File must be smaller than 10MB!');
                return false;
            }
            return false;
        },
        onRemove: (file) => {
            setFileList(fileList.filter(item => item.uid !== file.uid));
        },
    };

    const documentsColumns = [
        {
            title: 'Document Name',
            dataIndex: 'name',
            key: 'name',
            width: 250,
            ellipsis: true,
            render: (text, record) => (
                <div style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</div>
                    <div style={{ fontSize: '12px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.fileName}</div>
                </div>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 130,
            ellipsis: true,
            render: (type) => <Tag color="blue" style={{ whiteSpace: 'nowrap' }}>{type}</Tag>,
            filters: [
                { text: 'ID Proof', value: 'ID Proof' },
                { text: 'Financial', value: 'Financial' },
                { text: 'Education', value: 'Education' },
                { text: 'Experience', value: 'Experience' },
                { text: 'Medical', value: 'Medical' },
                { text: 'Legal', value: 'Legal' },
                { text: 'Insurance', value: 'Insurance' },
                { text: 'Tax', value: 'Tax' },
                { text: 'Property', value: 'Property' },
                { text: 'Other', value: 'Other' },
            ],
            onFilter: (value, record) => record.type === value,
        },
        {
            title: 'Upload Date',
            dataIndex: 'uploadDate',
            key: 'uploadDate',
            width: 140,
            ellipsis: true,
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{new Date(text).toLocaleDateString()}</span>,
            sorter: (a, b) => new Date(a.uploadDate) - new Date(b.uploadDate),
        },
        {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
            width: 100,
            ellipsis: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            ellipsis: true,
            render: (status) => (
                <Tag color={getDocumentStatusColor(status)} style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                    {status}
                </Tag>
            ),
            filters: [
                { text: 'Verified', value: 'verified' },
                { text: 'Pending', value: 'pending' },
                { text: 'Rejected', value: 'rejected' },
                { text: 'Expired', value: 'expired' },
                { text: 'Renewal Required', value: 'renewal-required' },
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Document">
                        <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => handleView(record)}
                            size="small"
                        />
                    </Tooltip>
                    {!profileLocked && (
                        <>
                            <Tooltip title="Edit Document">
                                <Button
                                    type="link"
                                    icon={<EditOutlined />}
                                    onClick={() => showModal(record)}
                                    size="small"
                                />
                            </Tooltip>
                            <Tooltip title="Download Document">
                                <Button
                                    type="link"
                                    icon={<DownloadOutlined />}
                                    onClick={() => handleDownload(record)}
                                    size="small"
                                />
                            </Tooltip>
                            <Tooltip title="Delete Document">
                                <Popconfirm
                                    title="Are you sure you want to delete this document?"
                                    onConfirm={() => handleDelete(record)}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        type="link"
                                        icon={<DeleteOutlined />}
                                        danger
                                        size="small"
                                    />
                                </Popconfirm>
                            </Tooltip>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    // ==================== ATTENDANCE SECTION ====================

    const organization = useSelector((state) => state.auth.organization);

    const loadAttendance = async (page = 1, pageSize = 10, searchFilters = {}) => {
        if (!employeeId) return;
        setAttendanceLoading(true);
        try {
            const response = await actions.loadAttendanceRecords(
                page,
                pageSize,
                organization,
                { employee: employeeId, ...searchFilters }
            );
            if (response && response.results) {
                setAttendanceData(response.results);
                setAttendancePagination({
                    current: page,
                    pageSize: pageSize,
                    total: response.count,
                });
            }
        } catch (error) {
            message.error('Failed to load attendance records');
            console.error('Error loading attendance:', error);
        } finally {
            setAttendanceLoading(false);
        }
    };

    useEffect(() => {
        if (employeeId && organization && activeSection === 'attendance') {
            loadAttendance(urlFilters.page, Math.min(urlFilters.pageSize, MAX_PAGE_SIZE), attendanceFilters);
        }
    }, [employeeId, organization, activeSection, urlFilters.page, urlFilters.pageSize, attendanceFilters]);

    const handleAttendanceTableChange = (newPagination) => {
        // A new page size starts again from page 1
        const sizeChanged = newPagination.pageSize !== urlFilters.pageSize;
        setUrlFilters({ page: sizeChanged ? 1 : newPagination.current, pageSize: newPagination.pageSize });
    };

    const handleAttendanceSearch = (values = {}) => {
        setUrlFilters({
            status: values.status || null,
            range: values.dateRange && values.dateRange.length === 2 ? values.dateRange : null,
            page: 1,
        });
    };

    const statusColors = {
        present: 'green',
        absent: 'red',
        leave: 'orange',
        holiday: 'blue',
        partial: 'gold',
        late: 'volcano',
        early: 'purple',
        hours_deficit: 'magenta',
        weekend: 'cyan',
    };

    const attendanceColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            sorter: true,
        },
        {
            title: 'Shift',
            dataIndex: 'shift_name',
            key: 'shift',
            render: (text) => text || '-',
        },
        {
            title: 'Check In',
            dataIndex: 'first_in',
            key: 'first_in',
            render: (text) => text || '-',
        },
        {
            title: 'Check Out',
            dataIndex: 'last_out',
            key: 'last_out',
            render: (text) => text || '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={statusColors[status] || 'default'}>
                    {status?.replace(/_/g, ' ').toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Working Hours',
            dataIndex: 'total_hours_worked',
            key: 'total_hours_worked',
            render: (hours) => hours || '0',
        },
        {
            title: 'Overtime',
            dataIndex: 'overtime_hours',
            key: 'overtime_hours',
            render: (hours) => hours && parseFloat(hours) > 0 ? <Tag color="purple">{hours}</Tag> : '-',
        },
    ];

    // ==================== RENDER SECTIONS ====================

    const renderDocuments = () => {
        return (
            <>
                {/* {profileLocked && (
                    <Alert
                        message="Profile Locked"
                        description="Document upload, download, and deletion functionality is disabled while your profile is locked."
                        type="warning"
                        showIcon
                        style={{ marginBottom: '20px' }}
                    />
                )} */}
                <Card
                    // title="My Documents"
                    extra={
                        <Space>
                            <Popover
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                        <span style={{ fontSize: 14, fontWeight: 600 }}>Required Documents Checklist</span>
                                    </div>
                                }
                                content={
                                    <div style={{ width: 360, maxHeight: 320, overflowY: 'auto' }}>
                                        {[
                                            { label: 'ID Proof', desc: 'Aadhar Card', tag: 'Required', color: '#f5222d' },
                                            { label: 'Permanent Address Proof', desc: 'Aadhar Card / Rental Agreement / Electricity Bill / Gas Bill', tag: 'Required', color: '#f5222d' },
                                            { label: 'Tax Proof', desc: 'PAN Card', tag: 'Required', color: '#f5222d' },
                                            { label: 'Financial Document', desc: 'Bank Passbook / Bank Statement', tag: 'Required', color: '#f5222d' },
                                            { label: 'Education Proof', desc: 'Degree Certificates, Marksheets', tag: 'Required', color: '#f5222d' },
                                            { label: 'Experience', desc: 'Offer Letters, Relieving Letters', tag: 'Required', color: '#f5222d' },
                                            { label: 'Medical', desc: 'Fitness Certificate from any valid physician', tag: 'Required', color: '#f5222d' },
                                            { label: 'Passport Size Photo', desc: 'Recent photograph', tag: 'Required', color: '#f5222d' },
                                            { label: 'Current Address Proof', desc: 'If different from permanent address', tag: 'Optional', color: '#8c8c8c' },
                                        ].map((item, i) => (
                                            <div key={i} style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                                                padding: '8px 0', borderBottom: i < 8 ? '1px solid #f0f0f0' : 'none'
                                            }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13, color: '#262626' }}>{item.label}</div>
                                                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>{item.desc}</div>
                                                </div>
                                                <Tag color={item.color} style={{ marginLeft: 8, flexShrink: 0, fontSize: 11 }}>{item.tag}</Tag>
                                            </div>
                                        ))}
                                    </div>
                                }
                                placement="bottomRight"
                                trigger="hover"
                                overlayStyle={{ maxWidth: 420 }}
                            >
                                <Button type="default" icon={<InfoCircleOutlined />}>Info</Button>
                            </Popover>
                            {!profileLocked && (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => showModal(null)}
                                >
                                    Upload Document
                                </Button>
                            )}
                        </Space>
                    }
                >
                    <Table
                        columns={documentsColumns}
                        dataSource={documents}
                        loading={documentsLoading}
                        rowKey="id"
                        pagination={{
                            defaultPageSize: 20,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        scroll={{ x: 1000, y: 400 }}
                    />
                </Card>

                <Modal
                    title={editingDocument ? "Update Document" : "Upload Document"}
                    open={isModalVisible}
                    onCancel={handleCancel}
                    footer={null}
                    width={600}
                >
                    <Form form={form} layout="vertical" onFinish={handleSubmit}>
                        <Form.Item
                            label="Document Name"
                            name="name"
                            rules={[{ required: true, message: 'Please enter document name!' }]}
                        >
                            <Input placeholder="Enter document name" />
                        </Form.Item>

                        <Form.Item
                            label="Document Type"
                            name="type"
                            rules={[{ required: true, message: 'Please select document type!' }]}
                        >
                            <Select placeholder="Select document type">
                                <Option value="ID Proof">ID Proof</Option>
                                <Option value="Financial">Financial</Option>
                                <Option value="Education">Education</Option>
                                <Option value="Experience">Experience</Option>
                                <Option value="Medical">Medical</Option>
                                <Option value="Legal">Legal</Option>
                                <Option value="Insurance">Insurance</Option>
                                <Option value="Tax">Tax</Option>
                                <Option value="Property">Property</Option>
                                <Option value="Other">Other</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item label="Description (Optional)" name="description">
                            <Input.TextArea
                                rows={3}
                                placeholder="Add any additional description about the document..."
                            />
                        </Form.Item>

                        <Form.Item label={editingDocument ? "Replace File (Optional)" : "Upload File"}>
                            {editingDocument && (
                                <Alert
                                    message={`Current file: ${editingDocument.fileName}`}
                                    type="info"
                                    style={{ marginBottom: 8 }}
                                />
                            )}
                            <Dragger {...uploadProps}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                                <p className="ant-upload-hint">
                                    Support for PDF and image files. Maximum file size is 10MB.
                                </p>
                            </Dragger>
                        </Form.Item>

                        <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                            <Space>
                                <Button onClick={handleCancel}>Cancel</Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitLoading}
                                    disabled={!editingDocument && fileList.length === 0}
                                >
                                    {editingDocument ? "Update Document" : "Upload Document"}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </>
        );
    };

    const renderAttendance = () => {
        return (
            <Card 
            // title="Attendance Records"
            >
                <Space style={{ marginBottom: '16px' }} wrap>
                    <Select
                        allowClear
                        style={{ width: 150 }}
                        value={urlFilters.status || undefined}
                        onChange={(value) => {
                            handleAttendanceSearch({ status: value, dateRange: urlFilters.range });
                        }}
                        placeholder="Filter by status"
                    >
                        <Option value="present">Present</Option>
                        <Option value="absent">Absent</Option>
                        <Option value="leave">Leave</Option>
                        <Option value="holiday">Holiday</Option>
                        <Option value="partial">Partial Day</Option>
                    </Select>

                    <RangePicker
                        format="YYYY-MM-DD"
                        value={urlFilters.range}
                        onChange={(dates) => {
                            handleAttendanceSearch({ dateRange: dates, status: attendanceFilters.status });
                        }}
                    />

                    <Button
                        icon={<CalendarOutlined />}
                        onClick={() => {
                            const dates = [dayjs().startOf('month'), dayjs().endOf('month')];
                            handleAttendanceSearch({ dateRange: dates, status: attendanceFilters.status });
                        }}
                    >
                        This Month
                    </Button>

                    <Button
                        onClick={() => {
                            handleAttendanceSearch({});
                        }}
                    >
                        Clear
                    </Button>
                </Space>

                <Table
                    columns={attendanceColumns}
                    dataSource={attendanceData}
                    loading={attendanceLoading}
                    rowKey="id"
                    pagination={{
                        ...attendancePagination,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    }}
                    onChange={handleAttendanceTableChange}
                    scroll={{ x: 800 }}
                    size="middle"
                />
            </Card>
        );
    };

    const getSectionTitle = () => {
        const titles = {
            'documents': 'Documents',
            'attendance': 'Attendance'
        };
        return titles[activeSection] || 'HR Information';
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'documents':
                return renderDocuments();
            case 'attendance':
                return renderAttendance();
            default:
                return <Text>Section not found</Text>;
        }
    };

    return (
        <div style={{ height: 'calc(100vh - 280px)', padding: '20px' }}>
            <Card
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                bodyStyle={{ flex: 1, padding: 0, overflow: 'hidden' }}
            >
                <Layout style={{ flex: 1, background: '#fff', overflow: 'hidden' }}>
                    {/* Sidebar */}
                    <Sider
                        width={240}
                        style={{
                            background: '#fff',
                            borderRight: '1px solid #f0f0f0',
                            overflow: 'auto'
                        }}
                    >
                        <style>
                            {`
                                .hr-sidebar-menu .ant-menu-item-selected {
                                    background-color: #1890ff !important;
                                    color: #fff !important;
                                }
                                .hr-sidebar-menu .ant-menu-item-selected .anticon {
                                    color: #fff !important;
                                }
                                .hr-sidebar-menu .ant-menu-item-selected::after {
                                    border-right-color: #1890ff !important;
                                }
                            `}
                        </style>
                        <Menu
                            mode="inline"
                            selectedKeys={[activeSection]}
                            onClick={({ key }) => setActiveSection(key)}
                            style={{ borderRight: 0 }}
                            items={menuItems}
                            className="hr-sidebar-menu"
                        />
                    </Sider>

                    {/* Content Area */}
                    <Content style={{ padding: '24px', overflow: 'auto', background: '#fff' }}>
                        <Title level={5} style={{ marginBottom: 24 }}>
                            {getSectionTitle()}
                        </Title>
                        {renderContent()}
                    </Content>
                </Layout>
            </Card>
        </div>
    );
};

export default HR;
